import React, { useEffect, useState } from 'react';
import { Gift, X, ArrowRight } from 'lucide-react';

/**
 * Premium floating CTA shown ONLY after the customer has completed their
 * first successful order. Sits at the bottom-right of the Rewards dashboard.
 *
 * - Black + gold luxury feel (NOT cash-app / casino).
 * - Soft gold glow halo, gentle entrance animation, dismissable.
 * - Dismissal persists per-customer via localStorage so it never feels spammy.
 * - On click → smoothly scrolls to the existing share-buttons-grid and
 *   pulses a gold ring on it for ~2s.
 *
 * Props:
 *   eligible: boolean  — true when customer.first_order_recorded === true
 *   customerKey: string — used to scope the dismissal flag (e.g. referral_code)
 */
const ReferralFloatingCTA = ({ eligible, customerKey }) => {
  const storageKey = `nofee_referral_cta_dismissed_${customerKey || 'anon'}`;
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!eligible) return;
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(storageKey) === '1';
    } catch (_) { /* noop */ }
    if (!dismissed) {
      // small delay so it feels intentional, not aggressive
      const t = setTimeout(() => {
        setVisible(true);
        // next tick → trigger entrance transition
        requestAnimationFrame(() => setMounted(true));
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [eligible, storageKey]);

  const dismiss = (e) => {
    e?.stopPropagation?.();
    setMounted(false);
    try { window.localStorage.setItem(storageKey, '1'); } catch (_) { /* noop */ }
    setTimeout(() => setVisible(false), 220);
  };

  const openShare = () => {
    const grid = document.querySelector('[data-testid="share-buttons-grid"]');
    if (grid) {
      grid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      grid.classList.add('ring-2', 'ring-gold-500/60', 'rounded-xl');
      setTimeout(() => {
        grid.classList.remove('ring-2', 'ring-gold-500/60', 'rounded-xl');
      }, 2000);
    }
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed z-40 bottom-5 right-5 sm:bottom-6 sm:right-6 transition-all duration-300 ease-out ${
        mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
      data-testid="referral-floating-cta"
      role="complementary"
      aria-label="Invite friends and earn gift credits"
    >
      {/* Soft gold glow halo */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -m-3 rounded-[28px] bg-gold-500/15 blur-2xl pointer-events-none"
      />

      <button
        type="button"
        onClick={openShare}
        data-testid="referral-floating-cta-button"
        className="relative group flex items-center gap-3 pl-3 pr-4 py-3 sm:pl-3.5 sm:pr-5 sm:py-3.5 rounded-2xl bg-gradient-to-b from-zinc-900 to-black border border-gold-500/40 hover:border-gold-400/70 shadow-[0_20px_50px_-20px_rgba(212,175,55,0.45)] hover:shadow-[0_24px_60px_-18px_rgba(212,175,55,0.6)] transition-all backdrop-blur-md"
      >
        {/* Gold gift icon disc */}
        <span
          aria-hidden="true"
          className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 flex items-center justify-center shadow-[inset_0_-2px_4px_rgba(0,0,0,0.25)]"
        >
          <Gift className="w-5 h-5 text-black" strokeWidth={2.25} />
        </span>

        {/* Copy */}
        <span className="flex flex-col items-start leading-tight pr-2 sm:pr-1">
          <span className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.18em] text-gold-300 font-bold">
            Invite Friends
          </span>
          <span className="text-[13.5px] sm:text-sm font-bold text-white whitespace-nowrap">
            Earn Gift Credits
          </span>
        </span>

        {/* Arrow */}
        <ArrowRight className="hidden sm:block w-4 h-4 text-gold-400 transition-transform group-hover:translate-x-0.5" />
      </button>

      {/* Dismiss */}
      <button
        type="button"
        onClick={dismiss}
        data-testid="referral-floating-cta-dismiss"
        aria-label="Dismiss invite suggestion"
        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/90 border border-white/15 hover:border-gold-500/50 flex items-center justify-center text-white/55 hover:text-white transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

export default ReferralFloatingCTA;
